import React, { useEffect, useState } from 'react';
import { Form, Input, Modal, Select, Switch } from 'antd';
import { apiGetMasterData } from '@services/MasterData/MasterDataService';

const { Option } = Select;

const TYPE_LABEL = {
  CN: 'Nhà máy',
  TQL: 'Tổ quản lý',
  DHK: 'Đồng hồ tổng',
  TUYEN: 'Mạng lưới',
  METER_NAME: 'Tên đồng hồ',
  METER_SIZE: 'Cỡ đồng hồ'
};

/**
 * Modal dùng chung cho tạo mới và chỉnh sửa.
 *
 * Props:
 *  - open          : boolean
 *  - onCancel      : fn
 *  - onSubmit      : fn(values)  — values đã bao gồm type
 *  - type          : string      — loại danh mục đang active
 *  - cnOptions     : array       — danh sách Nhà máy (để chọn cha)
 *  - initialValues : object|null — null = tạo mới; object = chỉnh sửa
 */
const ModalAdd = ({ open, onCancel, onSubmit, type, cnOptions = [], initialValues = null }) => {
  const [form] = Form.useForm();
  const isEditing = !!initialValues?.id;

  // Tổ quản lý options for DHK modal (cascaded by selected Nhà máy)
  const [tqlOpts, setTqlOpts] = useState([]);
  const [loadingTql, setLoadingTql] = useState(false);

  const fetchTqlByCn = async (cnValue) => {
    if (!cnValue) {
      setTqlOpts([]);
      return;
    }
    setLoadingTql(true);
    try {
      const res = await apiGetMasterData({
        type: 'TQL',
        parent_level_1_id: cnValue,
        page: 1,
        size: 1000
      });
      const data = res.data?.data ?? res.data ?? [];
      setTqlOpts(Array.isArray(data) ? data : []);
    } catch {
      setTqlOpts([]);
    } finally {
      setLoadingTql(false);
    }
  };

  // Reset form when modal opens
  useEffect(() => {
    if (!open) return;

    const defaults = {
      name: '',
      value: '',
      is_active: true,
      parent_level_1_id: undefined,
      parent_level_2_id: undefined,
      ...(initialValues ?? {})
    };

    form.resetFields();
    form.setFieldsValue(defaults);
    setTqlOpts([]);

    // For DHK edit: pre-load TQL options for the saved Nhà máy
    if (type === 'DHK' && defaults.parent_level_2_id) {
      fetchTqlByCn(defaults.parent_level_2_id);
    }
  }, [open]);

  // When Nhà máy changes inside DHK modal: cascade-reset Tổ quản lý
  const handleCnChange = (val) => {
    form.setFieldValue('parent_level_1_id', undefined);
    fetchTqlByCn(val);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onSubmit({ ...values, type });
    } catch {
      // validation errors shown inline
    }
  };

  return (
    <Modal
      title={
        isEditing ? `Cập nhật ${TYPE_LABEL[type] ?? type}` : `Thêm ${TYPE_LABEL[type] ?? type}`
      }
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      okText={isEditing ? 'Cập nhật' : 'Thêm mới'}
      cancelText="Huỷ"
      destroyOnClose>
      <Form layout="vertical" form={form} style={{ marginTop: 8 }}>
        {/* Tên */}
        <Form.Item
          name="name"
          label="Tên hiển thị"
          rules={[{ required: true, message: 'Vui lòng nhập tên' }]}>
          <Input placeholder="Nhập tên..." />
        </Form.Item>

        {/* Mã */}
        <Form.Item
          name="value"
          label="Mã (code)"
          rules={[{ required: true, message: 'Vui lòng nhập mã' }]}>
          <Input placeholder="Nhập mã..." />
        </Form.Item>

        {/* TQL → chọn Nhà máy cha */}
        {type === 'TQL' && (
          <Form.Item
            name="parent_level_1_id"
            label="Thuộc Nhà máy"
            rules={[{ required: true, message: 'Vui lòng chọn Nhà máy' }]}>
            <Select placeholder="Chọn Nhà máy" allowClear showSearch optionFilterProp="children">
              {cnOptions.map((opt) => (
                <Option key={opt.id} value={opt.value}>
                  {opt.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {/* DHK → chọn Nhà máy (cascade) → Tổ quản lý */}
        {type === 'DHK' && (
          <>
            <Form.Item
              name="parent_level_2_id"
              label="Thuộc Nhà máy"
              rules={[{ required: true, message: 'Vui lòng chọn Nhà máy' }]}>
              <Select
                placeholder="Chọn Nhà máy"
                allowClear
                showSearch
                optionFilterProp="children"
                onChange={handleCnChange}>
                {cnOptions.map((opt) => (
                  <Option key={opt.id} value={opt.value}>
                    {opt.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="parent_level_1_id"
              label="Thuộc Tổ quản lý"
              rules={[{ required: true, message: 'Vui lòng chọn Tổ quản lý' }]}>
              <Select
                placeholder={tqlOpts.length === 0 ? 'Chọn Nhà máy trước' : 'Chọn Tổ quản lý'}
                allowClear
                showSearch
                optionFilterProp="children"
                loading={loadingTql}
                disabled={!loadingTql && tqlOpts.length === 0}>
                {tqlOpts.map((opt) => (
                  <Option key={opt.id} value={opt.value}>
                    {opt.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </>
        )}

        {/* Trạng thái */}
        <Form.Item name="is_active" label="Trạng thái" valuePropName="checked">
          <Switch checkedChildren="Hoạt động" unCheckedChildren="Ngừng" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ModalAdd;
